import { PerformanceMetric, usePerformance } from '@/context/performance';
import { setChildAppLoaded } from '@customerio/hydra-nav-state';
import { useEffect, useRef, useState } from 'react';
import { AppBundle, AppBundleRoutes, getEntrypoint, registerPreloadPromise } from '../../utils/app-bundles';
import { PageLoader } from '@customerio/pluma-components/react';
import { AppBundleLoader } from '@/components/app-bundle-loader';
import { plumaContextValueStore, plumaThemeStore } from '@/components/connected-pluma-provider';
import { servicesApi } from '@/services/api';

// Types for dynamically loaded Journeys app
type JourneysAppInstance = {
  injectScripts: () => Promise<void>;
  create: () => void;
  destroy: () => void;
};

type JourneysAppConstructor = new (
  element: HTMLElement,
  options?: {
    hydraProps?: {
      scrollContainerSelector?: string;
      useNewWorkspacesUrls?: boolean;
      plumaContextValue?: typeof plumaContextValueStore;
      plumaTheme?: typeof plumaThemeStore;
    };
  },
) => JourneysAppInstance;

type JourneysModule = {
  App: JourneysAppConstructor;
  default?: unknown;
};

export default function Journeys() {
  return <JourneysBackgroundApp />;
}

export function JourneysNonBackgroundApp() {
  const ref = useRef(null);
  const appInstance = useRef<JourneysAppInstance | null>(null);
  const [loaded, setIsLoaded] = useState(false);
  const recordPerformanceMetric = usePerformance();

  useEffect(() => {
    setChildAppLoaded(false);
  }, []);

  useEffect(() => {
    const load = async () => {
      recordPerformanceMetric(PerformanceMetric.ChildAppLoadStart);
      const entrypoint = (await getEntrypoint(AppBundle.Journeys)) as JourneysModule;
      if (appInstance.current == null && ref.current != null) {
        appInstance.current = new entrypoint.App(ref.current, {
          hydraProps: {
            scrollContainerSelector: '#embedded-app-container',
            useNewWorkspacesUrls: true,
            plumaContextValue: plumaContextValueStore,
            plumaTheme: plumaThemeStore,
          },
        });

        await appInstance.current.injectScripts();
        setIsLoaded(true);
        appInstance.current.create();
      }
    };

    void load();

    return () => {
      appInstance.current?.destroy();
      appInstance.current = null;
      setIsLoaded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  performance.mark('hydra:journeys-non-background-app:render');

  return (
    <>
      <div
        className="journeys-app"
        ref={ref}
        style={{ display: loaded ? 'flex' : 'none', minWidth: 0, flex: 1, height: 'fit-content' }}
      ></div>
      {loaded ? null : <PageLoader />}
    </>
  );
}

export function JourneysBackgroundApp() {
  const renderJourneysApp = async (appWrapper: HTMLDivElement) => {
    const entrypoint = (await getEntrypoint(AppBundle.Journeys)) as JourneysModule;

    const appInstance = new entrypoint.App(appWrapper, {
      hydraProps: {
        scrollContainerSelector: '#embedded-app-container',
        useNewWorkspacesUrls: true,
        plumaContextValue: plumaContextValueStore,
        plumaTheme: plumaThemeStore,
      },
    });

    await appInstance.injectScripts();
    appInstance.create();

    return () => {
      appInstance.destroy();
    };
  };

  performance.mark('hydra:journeys-background-app:render');

  return (
    <AppBundleLoader
      appBundle={AppBundle.Journeys}
      className="journeys-app"
      style="height: fit-content;"
      renderApp={renderJourneysApp}
    />
  );
}

export const preloadJourneysAppBundle = () => {
  // If the URL contains other app bundles, don't preload Journeys
  // (doing by exclusion, rather than checking for the presence of Journeys, because Journeys is the default app bundle)
  const url = String(window.location.href);
  const otherAppBundles = Object.values(AppBundleRoutes).filter(
    (route) => route !== AppBundleRoutes[AppBundle.Journeys],
  );
  if (otherAppBundles.some((bundle) => url.includes(bundle))) {
    return;
  }

  const preloadJourneysApp = async () => {
    performance.mark('hydra:journeys-preload:init');
    const entrypoint = (await window.importShim(AppBundle.Journeys)) as JourneysModule;

    performance.mark('hydra:journeys-preload:importshim-entrypoint-ready');

    // The App constructor requires an element to be passed, but it's not
    // actually used if we don't call `create()`
    const dummyAppWrapper = document.createElement('div');
    const appInstance = new entrypoint.App(dummyAppWrapper);
    performance.mark('hydra:journeys-preload:inject-scripts');
    await appInstance.injectScripts();
    performance.mark('hydra:journeys-preload:inject-scripts-complete');
    dummyAppWrapper.remove();
    appInstance.destroy();
    return entrypoint;
  };

  registerPreloadPromise(AppBundle.Journeys, preloadJourneysApp);
};

// Preload some requests that are likely to be needed by the Journeys app.
// We rely on the cross-app-cache implemented on the requests to prevent duplicate requests.
//
// Requests added here should generally be bottlenecked by waiting for server response rather
// than by transfer time, so as not to cause delays in the initial load of the app themselves.
export const preloadJourneysAccountData = async () => {
  try {
    await servicesApi.getAccount();
  } catch {
    // The request might fail when the user is e.g. logged out. Ignore errors here.
  }
};
